import axios from 'axios';
import { getToken, clearToken } from './token';

/**
 * Alamat mesin aplikasi, ditentukan SAAT HALAMAN DIBUKA — bukan saat dipasang.
 *
 * Kalau halaman ini dibuka dari HP, kata "localhost" menunjuk ke HP itu
 * sendiri, bukan ke komputer tempat aplikasi berjalan. Dulu alamat ini ditulis
 * tetap di frontend/.env, akibatnya: diisi alamat komputer -> rusak waktu
 * dibuka di komputer itu sendiri dan waktu ganti wifi; diisi localhost ->
 * tidak bisa dibuka dari HP. Sekarang halaman memakai alamat yang sama dengan
 * alamat yang dipakai untuk membuka halaman itu, dengan port 8001.
 */
export const ASAL_API = (() => {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  const h = window.location.hostname;
  const port = window.location.port;

  // 1. Dibuka di komputer tempat aplikasi jalan.
  if (h === 'localhost' || h === '127.0.0.1') return env || 'http://localhost:8001';

  // 2. Mode pengembangan di jaringan lokal (React dev server selalu di 3000),
  //    mis. dibuka dari HP lewat alamat wifi. Mesin aplikasi ada di port 8001
  //    pada komputer yang sama.
  if (port === '3000') return `${window.location.protocol}//${h}:8001`;

  // 3. Sudah dipasang online. Kalau alamat mesin diisi saat pembangunan,
  //    pakai itu; kalau tidak, mesin dan halaman berada di satu alamat dan
  //    "/api" diteruskan oleh proxy di depannya.
  return env;
})();

const api = axios.create({
  baseURL: `${ASAL_API}/api`,
  timeout: 45000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      clearToken();
      window.location.href = '/login';
      return Promise.reject(err);
    }
    // Retry GET requests yang gagal karena jaringan/timeout (mis. server cold-start)
    const cfg = err.config || {};
    if (!err.response && cfg.method === 'get' && (cfg.__retryCount || 0) < 2) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      await new Promise((r) => setTimeout(r, 1500 * cfg.__retryCount));
      return api(cfg);
    }
    return Promise.reject(err);
  }
);

export const apiError = (err, fallback = 'Terjadi kesalahan') => {
  if (!err?.response) return 'Tidak bisa terhubung ke server. Periksa koneksi, lalu coba lagi.';
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return fallback;
};

export default api;
